import Vue from 'vue';
import dayjs from 'dayjs';
import _ from 'lodash';
import { EnumUtility } from '@codequality/vue-cli-base';

import { Eフェーズ種別 } from '@/openapi/enum';
import { Cデータ返済計画, E金融借入返済方法, YearMonthValue, データListItemResponse } from '@/openapi/user';

import 決算期Utility, { 決算期, 決算期TableHeader } from '@/classes/common/決算期Utility';
import 借入返済Utility from '@/classes/common/借入返済Utility';
import AppFormatUtility from '@/classes/common/AppFormatUtility';
import AbstractデータService, { ServiceType, 帳票データ } from '@/classes/service/AbstractデータService';
import 計画PlbscfService from '@/classes/service/計画/計画PlbscfService';
import 金融機関借入Service, {
  金融機関借入,
  金融機関借入ListItem,
} from '@/classes/service/分析計画/金融機関借入Service';
import { DAYJS_YM_NUMBER_FORMAT } from '@/classes/common/Constants';

// ------------------------------------------------------------------
// enums
// ------------------------------------------------------------------

/** 返済原資種別 */
export enum 返済原資Type {
  FCF = 'FCF',
  原資明細 = '返済原資明細',
  原資合計 = '返済原資',
  返済率 = '返済率',
  額合計 = '返済額合計',
}

/** 開始時期選択種別 */
enum 開始時期選択Type {
  リスケ = 'リスケ開始時期',
  返済 = '返済開始時期',
}

/** 金融機関借入種別 */
export enum 金融機関返済Type {
  期首残高 = '期首残高',
  新規借入 = '新規借入',
  返済額 = '返済額',
  期末残高 = '期末残高',
  残高シェア = '残高シェア',
}

/** セル種別 */
export enum CellTagType {
  inputCurrency,
  inputPercent,
  inputText,
  currency,
  textLeft,
  textRight,
}

/** 返済期間種別 */
enum 返済期間Type {
  通常,
  リスケ,
  返済,
}

// ------------------------------------------------------------------
// types
// ------------------------------------------------------------------

/** parse の返却値 */
export type 返済計画 = {
  データpk?: number;
  cデータ返済計画: Cデータ返済計画;
  header: 決算期TableHeader[];
  返済原資List: 返済原資ListItem[]; // size: 行数, FCFから返済額合計まで 9行固定
  開始時期選択List: 開始時期ListItem[]; // size: リスケ, 返済の2行固定
  金融機関CalcList: 金融機関CalcListItem[]; // 金融機関の計算/入力用一覧
  金融機関GroupList: 金融機関GroupItem[]; // 金融機関の画面表示/帳票用一覧
};

/** 返済原資表の行情報 */
export type 返済原資ListItem = {
  type: 返済原資Type;
  label: 返済原資ListItemCell;
  values: 返済原資ListItemCell[];
};

/** 返済原資表のセル情報 */
export type 返済原資ListItemCell = {
  tagType: CellTagType;
  fix?: string | number;
  reference?: {
    object: Record<string, unknown>;
    key: string | number;
  };
  unit?: string;
};

/** リスケ/返済開始時期の行情報 */
type 開始時期ListItem = {
  type: 開始時期選択Type;
  reference: {
    object: Cデータ返済計画;
    key: keyof Cデータ返済計画;
  };
};

/** 計算用の各金融機関/合計情報 */
type 金融機関CalcListItem = {
  直近月: {
    残高: number;
    シェア: number;
    有効残高: number;
    有効残高シェア: number;
    前期末残高: number;
  };
  list: 金融機関返済CalcListItem[];
  金融機関借入?: 金融機関借入ListItem; // データが存在しない場合は合計行
};

/** 計算用の金融機関返済計画の行情報 */
type 金融機関返済CalcListItem = {
  type: 金融機関返済Type;
  values: 金融機関返済CalcCell[];
};

/** 計算用の金融機関返済計画のセル情報 */
type 金融機関返済CalcCell = {
  fix: number | string; // 計算値/ラベル
  ymInput?: YearMonthValue; // Cデータ金融機関借入の返済額
  決算期ym: number;
  monthlyValues: YearMonthValue[];
};

/** 画面/帳票の金融機関表の行情報 */
export type 金融機関ListItem = {
  借入?: 金融機関借入ListItem;
  isTotal: boolean;
  type: 金融機関返済Type;
  cells: (金融機関Cell | null)[];
};

/** 画面/帳票の金融機関表のセル設定 */
export type 金融機関Cell = Partial<金融機関返済CalcCell> & {
  tagType: CellTagType;
  rowspan: number;
  colspan: number;
  unit: string;
  isDisabled: boolean;
};

export type 金融機関GroupItem = {
  calcList: 金融機関CalcListItem[]; // 生成元となった「金融機関CalcList」の行を参照
  summary: 金融機関CalcListItem; // 各「金融機関CalcList」の値を纏めたデータ
  isTotal: boolean;
  list: 金融機関ListItem[];
  原資返済額合計s: number[];
};

export type YMValue = YearMonthValue & { oldValue?: number };

// ------------------------------------------------------------------
// 返済計画Service
// ------------------------------------------------------------------

export default class 返済計画Service extends AbstractデータService<返済計画> {
  // ------------------------------------------------------------------
  // fields
  // ------------------------------------------------------------------

  protected serviceType = ServiceType.返済計画;

  /** 原資明細行数 */
  private readonly 原資明細ListItemCount = 5;

  /** 計画PlbscfService */
  private 計画PlbscfService: 計画PlbscfService | null = null;

  /** 金融機関借入Service */
  private 金融機関借入Service: 金融機関借入Service | null = null;

  /** 決算期 */
  private 決算期: 決算期;

  /** 決算期毎の集計期間年月一覧 */
  private 集計期間Yms: number[][] = [];

  // ------------------------------------------------------------------
  // constructor
  // ------------------------------------------------------------------

  public constructor(eフェーズ種別: Eフェーズ種別 | null = null) {
    super(eフェーズ種別);

    this.決算期 = 決算期Utility.get計画決算期(0, true);
  }

  // ------------------------------------------------------------------
  // override
  // ------------------------------------------------------------------

  /**
   * @override
   * @param データs
   * @param option
   * @returns
   */
  public parse(データs: データListItemResponse[], option?: Record<string, AbstractデータService<unknown>>): 返済計画 {
    // 各service初期化
    if (this.計画PlbscfService === null) {
      this.計画PlbscfService =
        option?.計画PlbscfService === undefined
          ? new 計画PlbscfService(this.eフェーズ種別)
          : (option?.計画PlbscfService as 計画PlbscfService);
    }
    this.計画PlbscfService.set勘定科目s = this.勘定科目s;
    this.計画PlbscfService.set部門s = this.部門s;

    if (this.金融機関借入Service === null) {
      this.金融機関借入Service =
        option?.金融機関借入Service === undefined
          ? new 金融機関借入Service()
          : (option?.金融機関借入Service as 金融機関借入Service);
    }

    // 各serviceのparse
    this.計画PlbscfService.parse(データs); // 別途データ取得するため返り値は不要
    const v金融機関借入 = this.金融機関借入Service.parse(データs);

    // Cデータ返済計画生成
    const 返済計画データ = this.findBy入力分類(データs);
    const cデータ返済計画 =
      返済計画データ !== undefined
        ? (JSON.parse(返済計画データ.cデータ) as Cデータ返済計画)
        : this.createInitialCデータ返済計画();

    // 返済原資List生成
    const 返済原資List = this.create返済原資List(cデータ返済計画);

    // 開始時期選択List生成
    const 開始時期選択List = this.create開始時期選択List(cデータ返済計画);

    // 金融機関List生成
    const 金融機関CalcList = this.create金融機関CalcList(v金融機関借入);

    // 返済計画設定
    const v返済計画: 返済計画 = {
      データpk: 返済計画データ?.pk,
      cデータ返済計画,
      header: this.決算期.header,
      返済原資List,
      開始時期選択List,
      金融機関CalcList,
      金融機関GroupList: [], // calculate金融機関返済で詰める
    };

    // 初回計算
    for (let i = 0; i < cデータ返済計画.yearlyValues.length; i++) {
      this.calculate返済原資(v返済計画, i, true);
    }
    this.calculate金融機関返済(v返済計画);

    return v返済計画;
  }

  /**
   * @override
   * @param データs
   * @param option
   * @returns
   */
  public parse帳票ListItemRequest(
    データs: データListItemResponse[],
    option?: Record<string, AbstractデータService<unknown>>
  ): 帳票データ {
    const raw = this.parse(データs, option);
    const 金融機関List = raw.金融機関GroupList.flatMap((group) => group.list);

    // テンプレート行設定
    const templateConfig = {
      返済原資List: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
      開始時期選択List: 10,
      金融機関list: {
        header: 11,
        row: {
          normal: [12, 13, 14, 15, 16],
          total: [17, 18, 19, 20, 21],
        },
      },
    };
    // 動的列設定
    const dynamicColumnConfigs = {
      disable: 5,
      enable: 6,
    };

    // タグタイプ、単位指定から値を取得する関数
    const getCellValueByTagType = (
      ref: unknown,
      cell: { tagType: CellTagType; fix?: string | number; unit?: string }
    ) => {
      let value = ref === undefined ? cell.fix : ref;
      if (cell.tagType === CellTagType.currency || cell.tagType === CellTagType.inputCurrency) {
        value = this.getMoneyUnitValue(Number(value));
      }
      if (cell.unit !== undefined) value += cell.unit;
      return value;
    };
    // 返済原資ListItemCellから値を取得する関数
    const getCellValueOf返済原資 = (cell: 返済原資ListItemCell) => {
      const ref = cell.reference?.object[cell.reference?.key];
      return getCellValueByTagType(ref, cell);
    };

    // 以下帳票データ整形
    // 初行設定込みで初期化
    const templateRows: number[] = templateConfig.返済原資List;
    const values: unknown[][] = [
      [AbstractデータService.getMoneyUnitLabel(), ...this.templateCellValues(4), ...raw.header.map((h) => h.label)],
    ];
    // 使用不可列数の取得
    const targetRow = 金融機関List.find((item) => !item.isTotal && item.type === 金融機関返済Type.返済額);
    const disabledCount =
      targetRow === undefined
        ? 0
        : targetRow.cells.filter((cell) => cell !== null && cell.決算期ym !== undefined && cell.isDisabled).length;
    // 動的列設定
    const dynamicConfigs = [
      {
        columns: [
          ..._.range(5),
          ...new Array(disabledCount).fill(dynamicColumnConfigs.disable),
          ...new Array(raw.header.length - disabledCount).fill(dynamicColumnConfigs.enable),
        ],
      },
    ];

    // 返済原資
    raw.返済原資List.forEach((item) => {
      const cellValues = [getCellValueOf返済原資(item.label), ...this.templateCellValues(4)];
      item.values.forEach((value) => cellValues.push(getCellValueOf返済原資(value)));
      values.push(cellValues);
    });

    // リスケ/返済開始
    raw.開始時期選択List.forEach((item) => {
      templateRows.push(templateConfig.開始時期選択List);
      const value = item.reference.object[item.reference.key] ?? null;
      values.push([item.type, value === null ? null : AppFormatUtility.format年月(Number(value))]);
    });

    // 金融機関返済
    templateRows.push(templateConfig.金融機関list.header);
    values.push([
      ...this.templateCellValues(4),
      `計画第0期\n直近月残高\n${AppFormatUtility.format年月(AbstractデータService.計画書.c実績直近月)}`,
      ...raw.header.map((h) => h.label),
    ]);

    金融機関List.forEach((item, index) => {
      const templateRowIndex = index % 5;
      templateRows.push(
        item.isTotal
          ? templateConfig.金融機関list.row.total[templateRowIndex]
          : templateConfig.金融機関list.row.normal[templateRowIndex]
      );
      const cellValues = item.cells.map((cell) =>
        cell === null ? null : getCellValueByTagType(cell.ymInput?.value, cell)
      );
      values.push(cellValues);
    });

    return {
      templateRows,
      values,
      dynamicConfigs,
    };
  }

  // ------------------------------------------------------------------
  // public methods
  // ------------------------------------------------------------------

  /**
   * 返済原資表の列データを計算をします
   *
   * @param v返済計画
   * @param isInitial
   */
  public calculate返済原資(v返済計画: 返済計画, colIndex: number, isInitial = false): void {
    // 返済原資Listから指定の返済原資Typeの行データを取得する関数
    const get返済原資ListItem = (type: 返済原資Type): 返済原資ListItem => {
      return v返済計画.返済原資List.find((l) => l.type === type)!;
    };

    // 計算対象取得
    const yearlyValue = v返済計画.cデータ返済計画.yearlyValues[colIndex];
    const fcf = get返済原資ListItem(返済原資Type.FCF);
    const 返済原資 = get返済原資ListItem(返済原資Type.原資合計);
    const 返済額合計 = get返済原資ListItem(返済原資Type.額合計);

    // 返済原資
    const 返済原資Fix = Number(fcf.values[colIndex].fix) + _.sum(yearlyValue.values);
    返済原資.values[colIndex].fix = 返済原資Fix;

    // 返済額合計
    const 返済額合計Fix = Math.round((返済原資Fix * yearlyValue.返済率) / 100);
    // 初回計算時以外にfixが更新された場合は返済額合計を削除する（未入力状態）
    if (!isInitial && 返済額合計.values[colIndex].fix !== 返済額合計Fix) {
      Vue.delete(yearlyValue, '返済額合計');
    }
    返済額合計.values[colIndex].fix = 返済額合計Fix;
  }

  /**
   * 金融機関返済情報の計算をします
   *
   * @param v返済計画
   */
  public calculate金融機関返済(v返済計画: 返済計画, isInitial = false, isCal = false): void {
    // 金融機関毎のリストと合計のリストを取得
    const 金融機関CalcList = v返済計画.金融機関CalcList.filter((item) => item.金融機関借入 !== undefined);
    const 合計CalcList = v返済計画.金融機関CalcList.find((item) => item.金融機関借入 === undefined)!;

    // リスケ/返済開始時期
    const リスケ開始Ym = this.get開始時期Ym(v返済計画, 開始時期選択Type.リスケ);
    const 返済開始Ym = this.get開始時期Ym(v返済計画, 開始時期選択Type.返済);

    // 手動入力値の設定
    金融機関CalcList.forEach((機関Item) => {
      const 借入 = 機関Item.金融機関借入!;
      if (isInitial) 借入.返済額 = [];

      // Cデータ金融機関借入の返済額を金融機関返済Listに格納
      機関Item.list.forEach((返済Item) => {
        if (返済Item.type === 金融機関返済Type.返済額) {
          返済Item.values.forEach((v) => {
            // const index = 借入.返済額.findIndex((input) => input.ym === v.決算期ym);
            // v.ymInput = index > -1 ? 借入.返済額[index] : undefined;

            const index = 借入.元本返済?.findIndex((input) => input.ym === _.floor(v.決算期ym / 100));
            v.ymInput = index != undefined && index > -1 && 借入.元本返済 ? 借入.元本返済[index] : undefined;
          });
        } else if (返済Item.type === 金融機関返済Type.新規借入) {
          返済Item.values.forEach((v) => {
            const index = 借入.新規借入?.findIndex((input) => input.ym === _.floor(v.決算期ym / 100));
            v.ymInput = index != undefined && index > -1 && 借入.新規借入 ? 借入.新規借入[index] : undefined;
          });
        }
      });
    });

    // 金融機関毎の決算期集計Map一覧
    const 金融機関Values = 金融機関CalcList.map((金融機関Item) => {
      const maps: Map<金融機関返済Type, 金融機関返済CalcCell>[] = [];
      for (let i = 0; i < this.集計期間Yms.length; i++) {
        // 参照渡しのためにオブジェクトごと設定
        const map = new Map(金融機関Item.list.map((返済Item) => [返済Item.type, 返済Item.values[i]]));
        maps.push(map);
      }
      return maps;
    });

    // 金融機関毎の決算期集計Mapのsetter関数
    const set金融機関FixValue = (
      金融機関Index: number,
      期Index: number,
      type: 金融機関返済Type,
      value: number
    ): void => {
      金融機関Values[金融機関Index][期Index].get(type)!.fix = value;
    };

    // 金融機関毎の決算期集計Mapのgetter関数
    const get金融機関FixValue = (金融機関Index: number, 期Index: number, type: 金融機関返済Type): number => {
      return Number(金融機関Values[金融機関Index][期Index].get(type)!.fix);
    };

    // 金融機関毎の月次集計Map一覧
    const 金融機関MonthlyValues = 金融機関CalcList.map((金融機関Item) => {
      const maps: Map<金融機関返済Type, YearMonthValue[]>[] = [];
      for (let i = 0; i < this.集計期間Yms.length; i++) {
        // 参照渡しのためにオブジェクトごと設定
        const map = new Map(金融機関Item.list.map((返済Item) => [返済Item.type, 返済Item.values[i].monthlyValues]));
        maps.push(map);
      }
      return maps;
    });

    // 金融機関毎の月次集計Mapのsetter関数
    const set金融機関MonthlyValue = (
      金融機関Index: number,
      期Index: number,
      月Index: number,
      type: 金融機関返済Type,
      value: number
    ): void => {
      金融機関MonthlyValues[金融機関Index][期Index].get(type)![月Index].value = value;
    };

    // 金融機関毎の月次集計Mapのgetter関数
    const get金融機関MonthlyValue = (
      金融機関Index: number,
      期Index: number,
      月Index: number,
      type: 金融機関返済Type
    ): number => {
      return 金融機関MonthlyValues[金融機関Index][期Index].get(type)![月Index].value;
    };

    const 直近月有効残高合計 = _.sum(金融機関CalcList.map((item) => item.直近月.有効残高));

    // 決算期単位の集計
    let 返済期間 = 返済期間Type.通常;
    const 前月金融機関s = 金融機関CalcList.map((item) => ({
      期末残高: item.直近月.残高,
      残高シェア: item.直近月.シェア,
    }));

    this.集計期間Yms.forEach((yms, 期Index) => {
      // 返済原資合計
      const 返済原資合計 = this.get返済額合計(v返済計画, 期Index);
      let total原資返済額 = 0;

      // 月単位の集計
      let 返済期間Count = yms.length;
      yms.forEach((ym, 月Index) => {
        // 返済期間の判定（リスケ開始と返済開始に同じ値を設定している場合を考慮してelseなしで判定）
        if (返済期間 === 返済期間Type.通常 || 返済期間 === 返済期間Type.リスケ) {
          if (リスケ開始Ym !== null && リスケ開始Ym <= ym) {
            返済期間 = 返済期間Type.リスケ;
          }
          if (返済開始Ym !== null && 返済開始Ym <= ym) {
            返済期間 = 返済期間Type.返済;
            返済期間Count = yms.length - 月Index;
          }
        }

        // 各金融機関の集計
        const ymDayjs = this.getDayjsFromYmNumber(ym);
        let total月次期末残高 = 0;
        金融機関CalcList.forEach((金融機関Item, 金融機関Index) => {
          const 借入 = 金融機関Item.金融機関借入!;
          const 直近月 = 金融機関Item.直近月;

          // 期首残高
          const 期首残高 = 前月金融機関s[金融機関Index].期末残高;
          set金融機関MonthlyValue(金融機関Index, 期Index, 月Index, 金融機関返済Type.期首残高, 期首残高);

          // 借入額
          let 借入額 = 0;
          if (借入.実行日 !== undefined && 借入.借入金額 !== undefined) {
            const 実行日 = dayjs(借入.実行日);
            if (ymDayjs.isSame(実行日, 'month')) 借入額 = 借入.借入金額;

            if (借入.新規借入) {
              const find新規借入 = 借入.新規借入.find((x) => x.ym === ym);
              if (find新規借入) {
                借入額 = find新規借入.value;
              }
            }
          }
          set金融機関MonthlyValue(金融機関Index, 期Index, 月Index, 金融機関返済Type.新規借入, 借入額);

          // 返済額
          let 返済額 = 0;
          switch (返済期間) {
            case 返済期間Type.通常:
              if (借入.実行日 === undefined || 借入.返済回数 === undefined) break;
              const 返済開始 = dayjs(借入.元本返済開始日 ?? 借入.実行日).date(1);
              const 返済回数 = ymDayjs.diff(返済開始, 'month') + 1;

              switch (借入.返済方法) {
                case E金融借入返済方法.元金均等:
                  // 初回返済
                  if (返済回数 === 1) {
                    返済額 = 借入.初回元本返済額 !== undefined ? 借入.初回元本返済額 : 0;
                  }
                  // 2回目以降返済
                  else if (1 < 返済回数 && 返済回数 < 借入.返済回数) {
                    返済額 = 借入.二回目以降元本返済額 !== undefined ? 借入.二回目以降元本返済額 : 0;
                  }
                  // 最終回返済
                  else if (返済回数 === 借入.返済回数) {
                    返済額 = 借入.最終回元本返済額 !== undefined ? 借入.最終回元本返済額 : 0;
                  }
                  break;

                case E金融借入返済方法.元利均等:
                  if (返済回数 < 1 || 借入.返済回数 < 返済回数) break;

                  const 当初金利 = 借入.当初金利!;
                  const 支払額 = 借入返済Utility.calc元利均等(当初金利, 借入.返済回数, 借入.借入金額!);
                  const 前月日数 = ymDayjs.subtract(1, 'month').daysInMonth();
                  const 利息 = 借入返済Utility.calc利息(期首残高, 当初金利, 前月日数, this.bnRoundingMode.実績計画);
                  返済額 = 支払額 - 利息;
                  break;

                case E金融借入返済方法.その他:
                  break; // ここには来ないはず
              }
              if (借入.元本返済) {
                const find返済額 = 借入.元本返済.find((x) => x.ym === ym);
                if (find返済額) {
                  返済額 = find返済額.value;
                }
              }
              break;
            case 返済期間Type.返済:
              if (直近月.残高 <= 0) break;

              // 返済原資合計からの返済分を算出（返済原資合計を返済期間数で割り、直近月の残高シェアで按分）
              const 残高シェア計算用 = this.calc残高シェア(直近月.有効残高, 直近月有効残高合計, 10);
              const 原資返済額 = this.calc原資返済額(返済原資合計, 残高シェア計算用);
              const monthlyValues = this.calcMonthlyValues(原資返済額, 返済期間Count);

              返済額 = 月Index < 11 ? monthlyValues[0] : _.last(monthlyValues)!;
              total原資返済額 += 返済額;
              break;
          }
          set金融機関MonthlyValue(金融機関Index, 期Index, 月Index, 金融機関返済Type.返済額, 返済額);

          // 期末残高
          const 期末残高 = 期首残高 + 借入額 - 返済額;
          set金融機関MonthlyValue(金融機関Index, 期Index, 月Index, 金融機関返済Type.期末残高, 期末残高);

          // 集計用変数に格納
          total月次期末残高 += 期末残高;
          前月金融機関s[金融機関Index].期末残高 = 期末残高;
        });

        // 期末残高合計から各金融機関の残高シェアを計算
        for (let i = 0; i < 金融機関CalcList.length; i++) {
          const 期末残高 = get金融機関MonthlyValue(i, 期Index, 月Index, 金融機関返済Type.期末残高);
          const 残高シェア = this.calc残高シェア(期末残高, total月次期末残高);
          set金融機関MonthlyValue(i, 期Index, 月Index, 金融機関返済Type.残高シェア, 残高シェア);
          前月金融機関s[i].残高シェア = 残高シェア;
        }
      });

      // 各金融機関の期末残高と期末残高合計算出
      let total期末残高 = 0;
      let is未調整 = true;
      const 返済原資差額 = 返済期間 === 返済期間Type.返済 ? 返済原資合計 - total原資返済額 : 0;
      金融機関CalcList.forEach((item, i) => {
        const map = 金融機関MonthlyValues[i][期Index];
        const sum = (type: 金融機関返済Type): number => {
          return _.sum(map.get(type)!.map((mv) => mv.value));
        };

        const 期首残高 =
          期Index === 0 ? item.直近月.残高 : get金融機関FixValue(i, 期Index - 1, 金融機関返済Type.期末残高);
        const 新規借入 = sum(金融機関返済Type.新規借入);

        // 返済額の調整
        const ym返済額 = item.list.find((i) => i.type === 金融機関返済Type.返済額)?.values[期Index].ymInput;
        let 返済額 = sum(金融機関返済Type.返済額);
        if (isCal && ym返済額 !== undefined) {
          ym返済額.value = 返済額;
        } else if (ym返済額 !== undefined && !isCal) {
          返済額 = ym返済額.value;
        }

        if (is未調整 && 返済額 > 0) {
          返済額 += 返済原資差額;
          is未調整 = false;

          if (返済開始Ym !== null) {
            // 「明細に転記」に備えて月割りの値も調整しておく
            const target = map
              .get(金融機関返済Type.返済額)!
              .filter((mv) => mv.ym >= 返済開始Ym)
              .find((mv) => mv.value > 0);
            if (target !== undefined) target.value += 返済原資差額;
          }
        }

        const 期末残高 = 期首残高 + 新規借入 - 返済額;
        total期末残高 += 期末残高;
        set金融機関FixValue(i, 期Index, 金融機関返済Type.期首残高, 期首残高);
        set金融機関FixValue(i, 期Index, 金融機関返済Type.新規借入, 新規借入);
        set金融機関FixValue(i, 期Index, 金融機関返済Type.返済額, 返済額);
        set金融機関FixValue(i, 期Index, 金融機関返済Type.期末残高, 期末残高);
      });

      // 各金融機関の残高シェアの調整
      for (let i = 0; i < 金融機関CalcList.length; i++) {
        // 残高シェア
        const 期末残高 = get金融機関FixValue(i, 期Index, 金融機関返済Type.期末残高);
        const 残高シェア = this.calc残高シェア(期末残高, total期末残高);
        set金融機関FixValue(i, 期Index, 金融機関返済Type.残高シェア, 残高シェア);
      }

      // 合計値の算出
      合計CalcList.list.forEach((item) => {
        const maps = 金融機関Values.map((v) => v[期Index]);
        const sum = (type: 金融機関返済Type): number => {
          const values = maps.map((m) => {
            const cell = m.get(type)!;
            return cell.ymInput !== undefined ? cell.ymInput.value : cell.fix;
          });
          return _.sum(values);
        };

        const 期首残高 = sum(金融機関返済Type.期首残高);
        const 新規借入 = sum(金融機関返済Type.新規借入);
        const 返済額 = sum(金融機関返済Type.返済額);
        let v = 0;
        switch (item.type) {
          case 金融機関返済Type.期首残高:
            v = 期首残高;
            break;
          case 金融機関返済Type.新規借入:
            v = 新規借入;
            break;
          case 金融機関返済Type.返済額:
            v = 返済額;
            break;
          case 金融機関返済Type.期末残高:
            v = 期首残高 + 新規借入 - 返済額;
            break;
          case 金融機関返済Type.残高シェア:
            v = 100;
            break;
        }
        item.values[期Index].fix = v;
      });
    });

    // 画面/帳票出力用の表示設定
    this.set金融機関List(v返済計画);
  }

  /**
   * 元本返済の値を計算
   * ※「明細に転記」用
   *
   * @param v返済計画
   */
  public calculate元本返済(v返済計画: 返済計画): void {
    const 返済開始Ym = this.get開始時期Ym(v返済計画, 開始時期選択Type.返済);

    v返済計画.金融機関GroupList
      .filter((group) => !group.isTotal)
      .forEach((group) => {
        const 直近月残高合計 = _.sum(group.calcList.map((item) => item.直近月.有効残高)); //期首残高
        const group返済額合計s = new Array<number>(this.集計期間Yms.length).fill(0);

        const monthlyValueTable = group.calcList.map((calc) =>
          calc.list
            .find((item) => item.type === 金融機関返済Type.返済額)!
            .values.flatMap((value) => _.cloneDeep(value.monthlyValues))
        );
        const get通常返済額合計 = (ym: number): number =>
          _.sum(monthlyValueTable.map((mvs) => mvs.find((mv) => mv.ym === ym)?.value ?? 0));

        const 返済額Cells = group.list
          .find((item) => item.type === 金融機関返済Type.返済額)!
          .cells.filter((cell) => cell !== null)
          .filter((cell) => cell?.決算期ym !== undefined);

        group.calcList.forEach((calc, index) => {
          const 残高シェア計算用 = this.calc残高シェア(calc.直近月.有効残高, 直近月残高合計, 10);
          const 元本返済s: YearMonthValue[] = [];

          calc.list
            .find((item) => item.type === 金融機関返済Type.返済額)!
            .values.forEach((cell, 期Index) => {
              const monthlyValues = cell.monthlyValues;
              let 返済額合計 = group.原資返済額合計s[期Index];
              let 返済原資MonthlyValues = monthlyValues.filter((mv) => 返済開始Ym !== null && mv.ym >= 返済開始Ym);

              const 返済額Cell = 返済額Cells[期Index]!;
              const ymInput = 返済額Cell.ymInput;
              if (ymInput !== undefined && 返済開始Ym !== null) {
                返済額合計 = ymInput.value;
                for (let i = 0; i < monthlyValues.length; i++) {
                  const ym = monthlyValues[i].ym;
                  if (ym >= 返済開始Ym) break;

                  const 通常返済額合計 = get通常返済額合計(ym);
                  if (返済額合計 < 通常返済額合計) {
                    返済原資MonthlyValues = [monthlyValues[i]];
                    monthlyValues.slice(i + 1).forEach((mv) => (mv.value = 0));
                    break;
                  } else {
                    返済額合計 -= 通常返済額合計;
                  }
                }
              }

              if (ymInput !== undefined || (ymInput === undefined && 期Index !== 0)) {
                let 原資返済額 = this.calc原資返済額(返済額合計, 残高シェア計算用);
                group返済額合計s[期Index] += 原資返済額;
                if (index === group.calcList.length - 1) 原資返済額 += 返済額合計 - group返済額合計s[期Index];

                this.calcMonthlyValues(原資返済額, 返済原資MonthlyValues.length).forEach(
                  (mv, i) => (返済原資MonthlyValues[i].value = mv)
                );
              }

              // 計画第0期の時は、月割りの値を入れる
              if (期Index === 0) {
                monthlyValues.forEach((mv) => 元本返済s.push(mv));
              } else {
                元本返済s.push({
                  ym: 決算期Utility.getYear(cell.決算期ym),
                  value: _.sum(monthlyValues.map((mv) => mv.value)),
                });
              }
            });

          calc.金融機関借入!.元本返済 = 元本返済s;
        });
      });
  }

  /**
   * 返済原資List更新
   *
   * @param v返済計画
   */
  public update返済原資List(v返済計画: 返済計画): void {
    this.計画PlbscfService!.calcAndCreate計画Plbscf();
    v返済計画.返済原資List = this.create返済原資List(v返済計画.cデータ返済計画);
  }

  // ------------------------------------------------------------------
  // private methods
  // ------------------------------------------------------------------

  /**
   * 初期状態のCデータ返済計画を生成します
   *
   * @returns Cデータ返済計画
   */
  private createInitialCデータ返済計画(): Cデータ返済計画 {
    return {
      返済原資Labels: new Array(this.原資明細ListItemCount).fill(''),
      yearlyValues: new Array(this.決算期.header.length).fill(null).map(() => ({
        values: new Array(this.原資明細ListItemCount).fill(0),
        返済率: 0,
        // 返済額合計は画面の入力時に追加
      })),
    };
  }

  /**
   * cデータ から 返済原資List を作成します
   *
   * @param cデータ
   * @returns 返済原資ListItem[]
   */
  private create返済原資List(cデータ: Cデータ返済計画): 返済原資ListItem[] {
    /** 返済原資の計算値セルの情報生成関数 */
    const createFixCell = (tagType: CellTagType, fix: string | number): 返済原資ListItemCell => {
      return {
        tagType,
        fix,
      };
    };

    /** 返済原資の参照値セル（入力用）の情報生成関数 */
    const createRefCell = <T extends Record<keyof T, unknown> | Record<keyof T, undefined>>(
      tagType: CellTagType,
      object: T,
      key: string | number,
      unit?: string
    ): 返済原資ListItemCell => {
      return {
        tagType,
        reference: {
          object,
          key,
        },
        unit,
      };
    };

    // FCF
    const fcfValues = this.決算期.years.map((y) => this.計画PlbscfService!.getValueByCodeAnd決算期年月('N704', y.year));
    const fcfListItem: 返済原資ListItem = {
      type: 返済原資Type.FCF,
      label: createFixCell(CellTagType.textLeft, 返済原資Type.FCF),
      values: fcfValues.map((v) => createFixCell(CellTagType.currency, v)),
    };

    // 原資明細
    const 原資明細ListItems: 返済原資ListItem[] = [];
    for (let i = 0; i < cデータ.返済原資Labels.length; i++) {
      const item = {
        type: 返済原資Type.原資明細,
        label: createRefCell(CellTagType.inputText, cデータ.返済原資Labels, i),
        values: cデータ.yearlyValues.map((v) => {
          return createRefCell(CellTagType.inputCurrency, v.values, i);
        }),
      };
      原資明細ListItems.push(item);
    }

    // 返済原資
    const 返済原資ListItem: 返済原資ListItem = {
      type: 返済原資Type.原資合計,
      label: createFixCell(CellTagType.textLeft, 返済原資Type.原資合計),
      values: Array(cデータ.yearlyValues.length)
        .fill(null)
        .map(() => {
          return createFixCell(CellTagType.currency, 0);
        }),
    };

    // 返済率
    const 返済率ListItem: 返済原資ListItem = {
      type: 返済原資Type.返済率,
      label: createFixCell(CellTagType.textLeft, 返済原資Type.返済率),
      values: cデータ.yearlyValues.map((v) => createRefCell(CellTagType.inputPercent, v, '返済率', '%')),
    };

    // 返済額合計
    const 返済額合計ListItem: 返済原資ListItem = {
      type: 返済原資Type.額合計,
      label: createFixCell(CellTagType.textLeft, 返済原資Type.額合計),
      values: cデータ.yearlyValues.map((v) => createRefCell(CellTagType.inputCurrency, v, '返済額合計')),
    };

    return [fcfListItem, ...原資明細ListItems, 返済原資ListItem, 返済率ListItem, 返済額合計ListItem];
  }

  /**
   * cデータ から 開始時期List を作成します
   *
   * @param cデータ
   * @returns 開始時期ListItem[]
   */
  private create開始時期選択List(cデータ: Cデータ返済計画): 開始時期ListItem[] {
    return [
      {
        type: 開始時期選択Type.リスケ,
        reference: {
          object: cデータ,
          key: 'リスケ開始時期',
        },
      },
      {
        type: 開始時期選択Type.返済,
        reference: {
          object: cデータ,
          key: '返済開始時期',
        },
      },
    ];
  }

  /**
   * 金融機関借入 から 金融機関CalcList を生成します
   *
   * @param v金融機関借入
   * @returns 金融機関CalcListItem[]
   */
  private create金融機関CalcList(v金融機関借入: 金融機関借入): 金融機関CalcListItem[] {
    const 直近月 = this.getDayjsFromYmNumber(AbstractデータService.計画書.c実績直近月);

    const 集計期間s = this.決算期.years.map((決算年) => {
      let monthCount = -1;
      let startY = -1;
      let startM = -1;

      const 決算期 = this.getDayjsFromYmNumber(決算年.year * 100 + this.決算期.fixMonth!);
      const 前期決算期 = 決算期.add(-1, 'year');
      const diff = 直近月.diff(前期決算期, 'month');
      if (diff <= 0) {
        // 直近月が前期決算期以前の場合は前期決算期翌月から当期決算期まで
        monthCount = 12;
        startY = 前期決算期.year();
        startM = 前期決算期.month() + 1;
      } else if (diff <= 12) {
        // 直近月が当期決算期以前の場合は直近月翌月から当期決算期まで
        monthCount = 決算期.diff(直近月, 'month');
        startY = 直近月.year();
        startM = 直近月.month() + 1;
      }

      const yms: number[] = [];
      for (let j = 0; j < monthCount; j++) {
        const ym = dayjs()
          .year(startY)
          .month(startM + j)
          .startOf('month')
          .format(DAYJS_YM_NUMBER_FORMAT);
        yms.push(Number(ym));
      }
      return { 決算期ym: Number(決算期.format(DAYJS_YM_NUMBER_FORMAT)), yms };
    });
    this.集計期間Yms = 集計期間s.map((集計期間) => 集計期間.yms);

    /** 金融機関返済Listの初期状態生成関数 */
    const createInitialCalcList = (): 金融機関返済CalcListItem[] => {
      return EnumUtility.keys(金融機関返済Type).map((key) => ({
        type: 金融機関返済Type[key as keyof typeof 金融機関返済Type],
        values: 集計期間s.map((集計期間) => ({
          fix: 0,
          決算期ym: 集計期間.決算期ym,
          monthlyValues: 集計期間.yms.map((ym) => ({ ym, value: 0 })),
        })),
      }));
    };

    // 各金融機関データ
    let total残高 = 0;
    let 有効残高合計 = 0;
    const 金融機関s: 金融機関CalcListItem[] = v金融機関借入.list
      .filter((item) => item.返済方法 !== E金融借入返済方法.その他)
      .map((item) => {
        const 残高 = item.直近月残高 !== undefined ? item.直近月残高 : 0;
        const 前期末残高 = item.前期末残高 !== undefined ? item.前期末残高 : 0;
        total残高 += 残高;
        if (残高 > 0) 有効残高合計 += 残高;
        return {
          直近月: {
            残高: 残高,
            シェア: 0,
            有効残高: 残高 > 0 ? 残高 : 0,
            有効残高シェア: 0,
            前期末残高: 前期末残高,
          },
          list: createInitialCalcList(),
          金融機関借入: item,
        };
      });

    // 合計データ
    const total: 金融機関CalcListItem = {
      直近月: {
        残高: total残高,
        シェア: 100,
        有効残高: 有効残高合計,
        有効残高シェア: 100,
        前期末残高: 0,
      },
      list: createInitialCalcList(),
    };

    // 各金融機関の直近月シェア設定
    金融機関s.forEach((item) => {
      if (total残高 === 0) return;
      item.直近月.シェア = this.calc残高シェア(item.直近月.残高, total残高);
      item.直近月.有効残高シェア = this.calc残高シェア(item.直近月.残高, 有効残高合計);
    });

    return [...金融機関s, total];
  }

  /**
   * 返済計画からリスケ/返済開始時期をnumberで取得します
   * 未設定の場合はnullを返却します
   *
   * @param v返済計画
   * @param type
   * @returns number | null
   */
  private get開始時期Ym(v返済計画: 返済計画, type: 開始時期選択Type): number | null {
    const ref = v返済計画.開始時期選択List.find((item) => item.type === type)!.reference;
    const ym = Number(ref.object[ref.key]);
    return Number.isNaN(ym) ? null : ym;
  }

  /**
   * 返済額合計を取得
   *
   * @param v返済計画
   * @param index
   * @returns
   */
  private get返済額合計(v返済計画: 返済計画, index: number): number {
    const 返済原資 = v返済計画.返済原資List.find((item) => item.type === 返済原資Type.額合計)!.values[index];
    const 返済原資Ref = 返済原資.reference?.object[返済原資.reference?.key];
    return Number(返済原資Ref !== undefined ? 返済原資Ref : 返済原資.fix);
  }

  /**
   * 指定Ym（DAYJS_YM_NUMBER_FORMAT）のdayjsオブジェクトを取得します
   *
   * @param ym
   * @returns dayjs
   */
  private getDayjsFromYmNumber(ym: number): dayjs.Dayjs {
    return dayjs(String(ym), DAYJS_YM_NUMBER_FORMAT);
  }

  /**
   * 原資返済額を計算
   *
   * @param 返済額
   * @param 残高シェア
   * @param count
   * @returns
   */
  private calc原資返済額(返済額合計: number, 残高シェア: number, count = 1): number {
    return this.calcPercentByBigNumber(返済額合計, 残高シェア / 100, count, this.bnRoundingMode.実績計画);
  }

  /**
   * 月割りの値を計算 ※端数は最終月で調整
   *
   * @param value
   * @param count
   * @returns
   */
  private calcMonthlyValues(value: number, count: number): number[] {
    const results = new Array<number>(count).fill(this.calc原資返済額(value, 100, count));
    results[results.length - 1] += value - _.sum(results);
    return results;
  }

  /**
   * 残高シェアを計算
   *
   * @param value
   * @param total
   * @returns
   */
  private calc残高シェア(value: string | number, total: string | number, decimalPlaces = 1): number {
    return this.calcPercentByBigNumber(
      value as number,
      100,
      total as number,
      this.bnRoundingMode.実績計画,
      decimalPlaces
    );
  }

  /**
   * 画面/帳票出力用の金融機関返済一覧を生成し、設定します。
   *
   * @param v返済計画
   */
  private set金融機関List(v返済計画: 返済計画): void {
    /** 画面表示/帳票用の金融機関一覧のセル生成関数 */
    const createCell = (
      tagType: CellTagType,
      cell: 金融機関返済CalcCell,
      rowspan = 1,
      colspan = 1,
      unit = '',
      isDisabled = false
    ): 金融機関Cell => {
      return {
        ...cell,
        tagType,
        colspan,
        rowspan,
        unit,
        isDisabled,
      };
    };

    /** 画面表示/帳票用の金融機関一覧の固定値セル生成関数 */
    const createFixCell = (
      tagType: CellTagType,
      fix: number | string,
      rowspan = 1,
      colspan = 1,
      unit = ''
    ): 金融機関Cell => {
      return {
        fix,
        tagType,
        colspan,
        rowspan,
        unit,
        isDisabled: false,
      };
    };

    // 返済開始時期
    const 返済開始時期Ym = this.get開始時期Ym(v返済計画, 開始時期選択Type.返済);

    const grouped = _.groupBy(v返済計画.金融機関CalcList, (item) => {
      const 借入 = item.金融機関借入;
      return 借入 === undefined ? '合計' : 借入.金融機関 + '_' + 借入.科目;
    });

    // 同一グループで同じ「返済額」を参照するようにする
    // Object.values(grouped).forEach((items) =>
    //   items.slice(1).forEach((item) => (item.金融機関借入!.返済額 = items[0].金融機関借入!.返済額))
    // );

    // 金融機関GroupList生成
    v返済計画.金融機関GroupList = Object.values(grouped).map((items) => {
      const 金融機関Item = _.cloneDeep(items[0]);
      const 直近月 = 金融機関Item.直近月;
      const 借入 = 金融機関Item.金融機関借入;
      const isTotal = 借入 === undefined;

      items.slice(1).forEach((item) => {
        直近月.残高 += item.直近月.残高;
        直近月.有効残高 += item.直近月.有効残高;

        item.list.forEach((返済) => {
          if (返済.type === 金融機関返済Type.残高シェア) return; // 残高シェアは後で再計算

          const targetType = 金融機関Item.list.find((item) => item.type === 返済.type)!;
          返済.values.forEach((value) => {
            const targetYm = targetType.values.find((cell) => cell.決算期ym === value.決算期ym)!;
            if (targetYm.ymInput !== undefined) {
              (targetYm.ymInput.value as number) += (value.ymInput?.value ?? value.fix) as number;
            } else {
              (targetYm.fix as number) += (value.ymInput?.value ?? value.fix) as number;
              targetYm.ymInput = undefined;
            }
          });
        });
      });

      // 直近月の残高シェアを再計算
      const 合計行 = v返済計画.金融機関CalcList.find((item) => item.金融機関借入 === undefined)!;
      直近月.シェア = this.calc残高シェア(直近月.残高, 合計行.直近月.残高);
      直近月.有効残高シェア = this.calc残高シェア(直近月.有効残高, 合計行.直近月.有効残高);
      const 残高シェア計算用 = this.calc残高シェア(直近月.有効残高, 合計行.直近月.有効残高, 10);

      const getCalcValues = (item: 金融機関CalcListItem, type: 金融機関返済Type): 金融機関返済CalcCell[] =>
        item.list.find((row) => row.type === type)!.values;

      const 期首残高Values = getCalcValues(金融機関Item, 金融機関返済Type.期首残高);
      const 新規借入Values = getCalcValues(金融機関Item, 金融機関返済Type.新規借入);
      const 返済額Values = getCalcValues(金融機関Item, 金融機関返済Type.返済額);
      const 期末残高Values = getCalcValues(金融機関Item, 金融機関返済Type.期末残高);
      // 各期の残高シェアは期末残高の合計を更新してから再計算

      this.集計期間Yms.forEach((yms, 期Index) => {
        const 決算期Ym = _.last(yms)!;

        // 返済額を再計算し、併せて期首残高/期末残高を更新
        const 期首残高 = 期Index === 0 ? 直近月.残高 : (期末残高Values[期Index - 1].fix as number);
        const 新規借入 = 新規借入Values[期Index].ymInput?.value ?? (新規借入Values[期Index].fix as number);

        const 返済額Value = 返済額Values[期Index];
        let 返済額 = 返済額Value.ymInput?.value ?? 0;
        if (返済額Value.ymInput === undefined) {
          let 年間原資返済額 = 0;
          let monthlyValues = items
            .flatMap((item) => getCalcValues(item, 金融機関返済Type.返済額)[期Index].monthlyValues)
            .filter((value) => value.ym >= yms[0]);

          if (返済開始時期Ym !== null) {
            const 返済額合計 = this.get返済額合計(v返済計画, 期Index);
            年間原資返済額 = 返済開始時期Ym > 決算期Ym ? 0 : this.calc原資返済額(返済額合計, 残高シェア計算用);
            monthlyValues = monthlyValues.filter((value) => value.ym < 返済開始時期Ym);
          }

          返済額 = 年間原資返済額 + _.sum(monthlyValues.map((value) => value.value));
        }

        期首残高Values[期Index].fix = 期首残高;
        新規借入Values[期Index].fix = 新規借入;
        返済額Values[期Index].fix = 返済額;
        期末残高Values[期Index].fix = 期首残高 + 新規借入 - 返済額;
      });

      const 原資返済額合計s = this.集計期間Yms.map((_, 期Index) =>
        this.calc原資返済額(this.get返済額合計(v返済計画, 期Index), 残高シェア計算用)
      );

      return { calcList: items, summary: 金融機関Item, isTotal, list: [], 原資返済額合計s };
    });

    const sum = (type: 金融機関返済Type, 期Index: number): number =>
      _.sum(
        v返済計画.金融機関GroupList
          .filter((group) => !group.isTotal)
          .map((group) => {
            const value = group.summary.list.find((row) => row.type === type)!.values[期Index];
            return value.ymInput?.value ?? value.fix;
          })
      );

    // 合計値の算出
    const 合計CalcItem = v返済計画.金融機関GroupList.find((item) => item.isTotal)!.summary;
    合計CalcItem.list
      .filter(
        (row) =>
          row.type === 金融機関返済Type.期首残高 ||
          row.type === 金融機関返済Type.新規借入 ||
          row.type === 金融機関返済Type.返済額
      )
      .forEach((row) =>
        row.values.forEach((value, index) => {
          value.fix = sum(row.type, index);
        })
      );

    this.集計期間Yms.forEach((_, index) => {
      const findCell = (type: 金融機関返済Type): 金融機関返済CalcCell =>
        合計CalcItem.list.find((row) => row.type === type)!.values[index];

      const 期首残高 = findCell(金融機関返済Type.期首残高).fix as number;
      const 新規借入 = findCell(金融機関返済Type.新規借入).fix as number;
      const 返済額 = findCell(金融機関返済Type.返済額).fix as number;

      findCell(金融機関返済Type.期末残高).fix = 期首残高 + 新規借入 - 返済額;
      findCell(金融機関返済Type.残高シェア).fix = 100;
    });

    // 各期残高シェアの再計算
    v返済計画.金融機関GroupList
      .filter((item) => !item.isTotal)
      .forEach((group) => {
        const 金融機関Item = group.summary;

        const getCalcValues = (item: 金融機関CalcListItem, type: 金融機関返済Type): 金融機関返済CalcCell[] =>
          item.list.find((row) => row.type === type)!.values;
        const 期末残高Values = getCalcValues(金融機関Item, 金融機関返済Type.期末残高);
        const 残高シェアValues = getCalcValues(金融機関Item, 金融機関返済Type.残高シェア);
        const 合計期末残高Values = getCalcValues(合計CalcItem, 金融機関返済Type.期末残高);

        this.集計期間Yms.forEach((_, 期Index) => {
          const 期末残高 = 期末残高Values[期Index].fix;
          const 合計期末残高 = 合計期末残高Values[期Index].fix;
          残高シェアValues[期Index].fix = this.calc残高シェア(期末残高, 合計期末残高);
        });
      });

    v返済計画.金融機関GroupList.forEach((group, index) => {
      const 金融機関Item = group.summary;
      const 直近月 = 金融機関Item.直近月;
      const 借入 = 金融機関Item.金融機関借入;
      const isTotal = 借入 === undefined;

      group.list = 金融機関Item.list.map((返済CalcItem): 金融機関ListItem => {
        const type = 返済CalcItem.type;
        const rowCount = 金融機関Item.list.length;
        const is期首残高Row = type === 金融機関返済Type.期首残高;
        const is返済額Row = type === 金融機関返済Type.返済額;
        const is残高シェアRow = type === 金融機関返済Type.残高シェア;

        // 金融機関/項番/科目（期首残高行のみ詰める）
        const labels: (金融機関Cell | null)[] = [];
        if (is期首残高Row) {
          if (借入 !== undefined) {
            // 金融機関行は金融機関情報を詰める
            const 名称 = 借入.金融機関 !== undefined ? 借入.金融機関 : '';
            labels.push(createFixCell(CellTagType.textLeft, 名称, rowCount));
            labels.push(createFixCell(CellTagType.textLeft, index + 1, rowCount));
            labels.push(createFixCell(CellTagType.textLeft, 借入.科目, rowCount));
          } else {
            // 合計行は「合計」の文字列のみ詰める
            labels.push(createFixCell(CellTagType.textLeft, '合計', rowCount, 3));
            labels.push(null, null);
          }
        } else {
          labels.push(null, null, null);
        }

        // 項目
        labels.push(createFixCell(CellTagType.textLeft, type));

        // 直近月残高（期首残高行のみ詰める）および 直近月シェア（残高シェア行のみ詰める）
        if (is期首残高Row) {
          labels.push(createFixCell(CellTagType.currency, 直近月.残高, rowCount - 1));
        } else if (is残高シェアRow) {
          labels.push(createFixCell(CellTagType.textRight, 直近月.シェア, 1, 1, '%'));
        } else {
          labels.push(null);
        }

        // 各金融機関返済Typeのデータ生成
        let values: 金融機関Cell[] = [];
        if (!isTotal && is返済額Row) {
          values = 返済CalcItem.values.map((v) => {
            const isDisabled = 返済開始時期Ym === null || v.決算期ym < 返済開始時期Ym;
            const tagType = isDisabled ? CellTagType.currency : CellTagType.inputCurrency;
            return createCell(tagType, v, 1, 1, '', isDisabled);
          });
        } else {
          values = 返済CalcItem.values.map((v) => {
            return is残高シェアRow
              ? createFixCell(CellTagType.textRight, v.fix, 1, 1, '%')
              : createFixCell(CellTagType.currency, v.fix);
          });
        }

        return {
          借入,
          isTotal,
          type,
          cells: [...labels, ...values],
        };
      });
    });
  }
}
